import React, { useEffect, useRef, useState } from 'react';

import { ContainerWidthContext } from './lib/ContainerWidthContext';
import { fetchSeoEmbed, fetchSeoEmbedAvatars } from './lib/api';
import EmbedLoader from './lib/EmbedLoader';
import EmbedError from './lib/EmbedError';

// The four layout views are loaded through dynamic import so the ESM build
// (famewall-widget.js, splitting: true) ships each layout as its own chunk —
// a wall embed never downloads Swiper, a carousel never downloads Muuri. The
// IIFE build (newFrame.js) inlines these imports, so there the behavior is
// unchanged (resolution is a microtask).
const VIEW_LOADERS = {
  grid: () => import('./views/SeoWallView'),
  carousel: () => import('./views/SeoCarouselView'),
  slider: () => import('./views/SeoSliderView'),
  avatars: () => import('./views/SeoAvatarsView'),
};

// Wall translations key off the visitor's language. Send it in the URL rather
// than leaning on the Accept-Language header: the CDN's cache key includes the
// query string but NOT that header, so a header-derived translation would be
// cached and then served to every visitor regardless of their language.
// Matches the SSR pages' `acceptLanguage.split(',')[0]` shape.
function browserLanguage() {
  try {
    if (typeof navigator === 'undefined') {
      return '';
    }

    const language =
      navigator.language || (navigator.languages && navigator.languages[0]) || '';

    return language ? String(language).trim() : '';
  } catch (err) {
    return '';
  }
}

// Stable component identities (React.lazy once at module scope, not per
// render) so remounts don't re-trigger Suspense.
const VIEWS = {
  grid: React.lazy(VIEW_LOADERS.grid),
  carousel: React.lazy(VIEW_LOADERS.carousel),
  slider: React.lazy(VIEW_LOADERS.slider),
  avatars: React.lazy(VIEW_LOADERS.avatars),
};

// Tracks the .famewall-embed container width so the renderers' breakpoints
// follow the embed box (the iframe versions follow the iframe viewport, which
// is the same thing).
//
// Forced-reflow note: ResizeObserver callbacks fire AFTER the browser has
// finished its layout pass, so the entry's contentRect / contentBoxSize is
// already populated and can be read for free. Calling getBoundingClientRect()
// inside the same callback would force a synchronous layout (Lighthouse
// flagged exactly that as a top reflow source). The fallback path (no
// ResizeObserver) is only reached on legacy browsers and uses the window
// resize event - the redundant window listener that the previous version
// kept alongside ResizeObserver was removed because it doubled the layout
// work on every resize.
//
// Zero-width note: a container that has not been laid out yet measures 0, and
// that used to be papered over with window.innerWidth. The breakpoints then
// believed they were on a desktop (cardWidth '31%') while the real box was
// still 0px wide, so every card resolved to 0px, wrapped one character per
// line, and Muuri packed them into a five-figure-pixel column - a blank slab
// that grows for a few seconds and never stops (reported from Weebly's Custom
// HTML element, measured at 0 x 35,797px before settling at 0 x 11,684px).
//
// So the hook now reports whether a *real* width has been seen. The caller
// holds the loader until it has; if one never arrives, the grace timer falls
// back to a floor that SeoEmbedApp also enforces on our own mount point, so
// the wall lands narrow-but-correct instead of invisible-but-enormous.
// None of this runs for a container that measures non-zero at mount, which is
// every embed rendering correctly today - that path is untouched.
const ZERO_WIDTH_GRACE_MS = 2500;
const ZERO_WIDTH_FLOOR = 320;

function measureHostWidth(hostElement) {
  if (hostElement && typeof hostElement.getBoundingClientRect === 'function') {
    const rect = hostElement.getBoundingClientRect();
    if (rect.width) {
      return Math.round(rect.width);
    }
  }

  return 0;
}

function useContainerWidth(hostElement) {
  const [size, setSize] = useState(() => {
    const measured = measureHostWidth(hostElement);

    if (measured) {
      return { width: measured, ready: true, floored: false };
    }

    return {
      width: typeof window !== 'undefined' ? window.innerWidth : 1024,
      ready: false,
      floored: false,
    };
  });

  useEffect(() => {
    if (!hostElement || typeof window === 'undefined') {
      return undefined;
    }

    const apply = (rawWidth) => {
      if (!rawWidth) {
        return;
      }
      const rounded = Math.round(rawWidth);
      setSize((current) =>
        current.ready && current.width === rounded
          ? current
          : // `floored` is sticky. On a shrink-to-fit container the min-width
            // we added is *why* the box became measurable; dropping it here
            // would collapse it back to 0 and restart the cycle.
            { width: rounded, ready: true, floored: current.floored },
      );
    };

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        let nextWidth = 0;
        if (entry.contentBoxSize) {
          const size = Array.isArray(entry.contentBoxSize)
            ? entry.contentBoxSize[0]
            : entry.contentBoxSize;
          nextWidth = size ? size.inlineSize : 0;
        }
        if (!nextWidth && entry.contentRect) {
          nextWidth = entry.contentRect.width;
        }

        apply(nextWidth);
      });

      observer.observe(hostElement);

      return () => {
        observer.disconnect();
      };
    }

    const measureFromRect = () => {
      const rect = hostElement.getBoundingClientRect();
      apply(rect.width);
    };

    measureFromRect();
    window.addEventListener('resize', measureFromRect);

    return () => {
      window.removeEventListener('resize', measureFromRect);
    };
  }, [hostElement]);

  // Never leave an embed sitting on the loader forever - a container that is
  // legitimately 0 (a closed accordion, a hidden tab) must still resolve. When
  // the container measures non-zero at mount this effect returns immediately
  // and no timer is ever created.
  useEffect(() => {
    if (size.ready || typeof window === 'undefined') {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSize((current) =>
        current.ready
          ? current
          : { width: ZERO_WIDTH_FLOOR, ready: true, floored: true },
      );
    }, ZERO_WIDTH_GRACE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [size.ready]);

  return size;
}

// Owns data fetching for one .famewall-embed element, then hands the raw
// payload to the matching faithful view.
export default function SeoEmbedApp({ config, apiOrigin, shadowHost }) {
  const [state, setState] = useState({ status: 'loading' });
  const {
    width: containerWidth,
    ready: widthReady,
    floored: widthFloored,
  } = useContainerWidth(shadowHost);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    // Warm the layout chunk while the data request is in flight, so the ESM
    // build doesn't pay chunk latency after the fetch resolves.
    const warmView = VIEW_LOADERS[config.format] || VIEW_LOADERS.grid;
    warmView().catch(() => {});

    let request;

    if (config.format === 'avatars') {
      request = fetchSeoEmbedAvatars(apiOrigin, {
        collection_uuid: config.collectionId,
      });
    } else {
      request = fetchSeoEmbed(apiOrigin, {
        wall_url: config.wallUrl,
        limit: config.limit,
        theme: config.theme,
        user_language: browserLanguage(),
      });
    }

    request
      .then((data) => {
        if (data && data.status) {
          setState({ status: 'ready', data });
        } else {
          setState({
            status: 'error',
            message: (data && data.msg) || 'Testimonials could not be loaded.',
          });
        }
      })
      .catch((err) => {
        setState({
          status: 'error',
          message: err && err.message ? err.message : 'Testimonials could not be loaded.',
        });
      });
  }, []);

  // Last resort for a container that never reports a width: floor our own
  // mount point inside the shadow root so the card percentages have something
  // real to resolve against. Never touches the customer's element, and only
  // ever runs once ZERO_WIDTH_GRACE_MS has expired with the box still at 0.
  useEffect(() => {
    if (!widthFloored || !shadowHost || !shadowHost.shadowRoot) {
      return;
    }

    const mountPoint = shadowHost.shadowRoot.querySelector('.famewall-embed-root');

    if (mountPoint) {
      mountPoint.style.minWidth = ZERO_WIDTH_FLOOR + 'px';
    }

    // And the host itself, so the recovered wall sits inside the customer's
    // box instead of overflowing a 0-width one (Weebly's .wcustomhtml computes
    // overflow-x: auto, which would otherwise clip it). Deliberately the
    // narrowest intervention available: a min-width, never a width, applied
    // only to an embed that has already been rendering nothing for
    // ZERO_WIDTH_GRACE_MS. It cannot shrink a container that has a width.
    if (shadowHost.style && !shadowHost.style.minWidth) {
      shadowHost.style.minWidth = ZERO_WIDTH_FLOOR + 'px';
    }
  }, [widthFloored, shadowHost]);

  if (state.status === 'loading') {
    return <EmbedLoader />;
  }

  if (state.status === 'error') {
    // Stay invisible on the host page unless the embed opts into debugging.
    return config.debug ? <EmbedError message={state.message} /> : null;
  }

  // The data is in, but laying the wall out against a container that has not
  // reported a width yet is exactly what produces the 0px-wide, 11,000px-tall
  // slab. Hold the loader - useContainerWidth guarantees this resolves within
  // ZERO_WIDTH_GRACE_MS even if the container never reports a width at all.
  if (!widthReady) {
    return <EmbedLoader />;
  }

  const View = VIEWS[config.format] || VIEWS.grid;

  return (
    <ContainerWidthContext.Provider value={containerWidth}>
      <React.Suspense fallback={<EmbedLoader />}>
        <View initialData={state.data} config={config} apiOrigin={apiOrigin} />
      </React.Suspense>
    </ContainerWidthContext.Provider>
  );
}
